import { useState } from 'react'
import { Eye, Users, ScrollText, MousePointerClick, Newspaper, Link2, ChevronLeft, ChevronRight, RotateCcw, ChartLine } from 'lucide-react'
import { Bar, BarChart, XAxis, YAxis, CartesianGrid, Legend, Tooltip, ResponsiveContainer } from 'recharts'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/shared/components/ui/chart'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { useEstadisticasPorNewsletter } from '../hooks/use-estadisticas'

const MESES = [
  { key: 'jan', label: 'Ene' },
  { key: 'feb', label: 'Feb' },
  { key: 'mar', label: 'Mar' },
  { key: 'apr', label: 'Abr' },
  { key: 'may', label: 'May' },
  { key: 'jun', label: 'Jun' },
  { key: 'jul', label: 'Jul' },
  { key: 'aug', label: 'Ago' },
  { key: 'sep', label: 'Sep' },
  { key: 'oct', label: 'Oct' },
  { key: 'nov', label: 'Nov' },
  { key: 'dec', label: 'Dic' },
]

const currentYear = new Date().getFullYear()
const currentMonth = new Date().getMonth() // 0-based

const chartConfig = {
  visitas: { label: 'Visitas', color: '#57a6d8' },   // blue (Tonal primary)
  scrolls: { label: 'Scrolls', color: '#8d82bc' },   // purple (Tonal secondary)
  clics: { label: 'Clics', color: '#2fbc80' },       // green (Tonal success)
} satisfies ChartConfig

function formatNewsletterName(key: string): string {
  // "mar-2026" → "Marzo 2026"
  const meses: Record<string, string> = {
    jan: 'Enero', feb: 'Febrero', mar: 'Marzo', apr: 'Abril',
    may: 'Mayo', jun: 'Junio', jul: 'Julio', aug: 'Agosto',
    sep: 'Septiembre', oct: 'Octubre', nov: 'Noviembre', dec: 'Diciembre',
  }
  const [mes, anio] = key.split('-')
  return `${meses[mes] || mes} ${anio || ''}`
}

export function EstadisticasPage() {
  useDocumentTitle("Estadísticas")
  const [mesIndex, setMesIndex] = useState(currentMonth) // 0-based
  const [anio, setAnio] = useState(currentYear)
  const [labFiltro, setLabFiltro] = useState<string>('todos')
  const { data, isLoading } = useEstadisticasPorNewsletter(anio, [MESES[mesIndex].key])

  const mesAnterior = () => {
    if (mesIndex === 0) {
      setMesIndex(11)
      setAnio(prev => prev - 1)
    } else {
      setMesIndex(prev => prev - 1)
    }
  }

  const mesSiguiente = () => {
    if (mesIndex === 11) {
      setMesIndex(0)
      setAnio(prev => prev + 1)
    } else {
      setMesIndex(prev => prev + 1)
    }
  }

  const resetMes = () => {
    setMesIndex(currentMonth)
    setAnio(currentYear)
  }

  const esActual = mesIndex === currentMonth && anio === currentYear

  // Consolidar datos de todos los newsletters del mes (normalmente será uno solo)
  const consolidated = data?.newsletters?.reduce(
    (acc, nl) => {
      // Sumar por laboratorio
      nl.por_laboratorio.forEach(lab => {
        const existing = acc.por_laboratorio.find(l => l.laboratorio === lab.laboratorio)
        if (existing) {
          existing.visitas += lab.visitas
          existing.scrolls += lab.scrolls
          existing.clics += lab.clics
        } else {
          acc.por_laboratorio.push({ ...lab })
        }
      })
      // Sumar usuarios
      nl.usuarios.forEach(u => {
        const existing = acc.usuarios.find(e => e.email === u.email)
        if (existing) {
          existing.visitas += u.visitas
          existing.scrolls += u.scrolls
          existing.clics += u.clics
        } else {
          acc.usuarios.push({ ...u })
        }
      })
      // Sumar links
      ;(nl.top_links || []).forEach(link => {
        const existing = acc.top_links.find(l => l.url_destino === link.url_destino)
        if (existing) {
          existing.clics += link.clics
          existing.usuarios_unicos += link.usuarios_unicos
        } else {
          acc.top_links.push({ ...link })
        }
      })
      return acc
    },
    {
      por_laboratorio: [] as { laboratorio: string; visitas: number; scrolls: number; clics: number }[],
      usuarios: [] as { email: string; laboratorio: string; visitas: number; scrolls: number; clics: number }[],
      top_links: [] as { url_destino: string; url_texto: string; clics: number; usuarios_unicos: number }[],
    }
  )

  const porLab = consolidated?.por_laboratorio.sort((a, b) => b.visitas - a.visitas) ?? []
  const laboratorios = [...new Set(consolidated?.usuarios.map(u => u.laboratorio) ?? [])].sort()
  const usuariosTodos = consolidated?.usuarios.sort((a, b) => b.visitas - a.visitas) ?? []
  const usuarios = (labFiltro === 'todos' ? usuariosTodos : usuariosTodos.filter(u => u.laboratorio === labFiltro)).slice(0, 30)
  const topLinks = consolidated?.top_links.sort((a, b) => b.clics - a.clics).slice(0, 10) ?? []

  const linksData = topLinks.map(link => ({
    nombre: link.url_texto || truncateUrl(link.url_destino),
    url_destino: link.url_destino,
    clics: link.clics,
    usuarios_unicos: link.usuarios_unicos,
  }))

  const tituloMes = formatNewsletterName(`${MESES[mesIndex].key}-${anio}`)

  const kpis = data?.kpis
  const hayDatos = porLab.length > 0 || usuarios.length > 0 || linksData.length > 0

  return (
    <div className="p-6 space-y-6">
      {/* Título + Filtro */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChartLine className="h-6 w-6 text-black dark:text-white" />
          <h1 className="text-2xl font-bold">Estadísticas Enterprise</h1>
        </div>
        <div className="inline-flex items-center gap-1 bg-white dark:bg-gray-800 rounded-lg px-1 py-1 border border-gray-300 dark:border-gray-700">
          <button
            onClick={mesAnterior}
            className="p-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="px-3 py-1 text-sm font-medium min-w-[140px] text-center">
            {formatNewsletterName(`${MESES[mesIndex].key}-${anio}`)}
          </span>
          <button
            onClick={mesSiguiente}
            className="p-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          {!esActual && (
            <button
              onClick={resetMes}
              className="p-1.5 rounded-md text-gray-500 hover:text-teal-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Volver al mes actual"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
        </div>
      ) : !hayDatos ? (
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Newspaper className="h-8 w-8 text-gray-400 mb-3" />
            <p className="text-gray-500">No hay datos para {tituloMes}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard icon={Eye} label="Total Visitas" value={kpis?.total_visitas ?? 0} />
            <KPICard icon={Users} label="Usuarios Únicos" value={kpis?.usuarios_unicos ?? 0} />
            <KPICard icon={ScrollText} label="Total Scrolls" value={kpis?.total_scrolls ?? 0} />
            <KPICard icon={MousePointerClick} label="Total Clics" value={kpis?.total_clics ?? 0} />
          </div>

          {/* Gráfica por laboratorio */}
          {porLab.length > 0 && (
            <Card className="border border-gray-300 dark:border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Engagement por Laboratorio</CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px] w-full">
                  <BarChart
                    data={porLab}
                    layout="vertical"
                    margin={{ left: 10, right: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="laboratorio"
                      type="category"
                      width={120}
                      tick={{ fontSize: 12 }}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Bar dataKey="visitas" fill="var(--color-visitas)" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="scrolls" fill="var(--color-scrolls)" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="clics" fill="var(--color-clics)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
          )}

          {/* Tabla de usuarios */}
          {usuariosTodos.length > 0 && (
            <Card className="border border-gray-300 dark:border-gray-700">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Usuarios</CardTitle>
                  <select
                    value={labFiltro}
                    onChange={e => setLabFiltro(e.target.value)}
                    className="text-sm border border-gray-300 dark:border-gray-700 rounded-md px-2 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                  >
                    <option value="todos">Todos los laboratorios</option>
                    {laboratorios.map(lab => (
                      <option key={lab} value={lab}>{lab}</option>
                    ))}
                  </select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="max-h-[400px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                        <TableHead>Email</TableHead>
                        <TableHead>Laboratorio</TableHead>
                        <TableHead className="text-right">Visitas</TableHead>
                        <TableHead className="text-right">Scrolls</TableHead>
                        <TableHead className="text-right">Clics</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {usuarios.map((u, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm">{u.email}</TableCell>
                          <TableCell className="text-sm text-gray-500">{u.laboratorio}</TableCell>
                          <TableCell className="text-right text-sm">{u.visitas}</TableCell>
                          <TableCell className="text-right text-sm">{u.scrolls}</TableCell>
                          <TableCell className="text-right text-sm">{u.clics}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Gráfica de Links Clickeados */}
          {linksData.length > 0 && (
            <Card className="border border-gray-300 dark:border-gray-700">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Link2 className="h-4 w-4 text-teal-500" />
                  <CardTitle className="text-base">Links Clickeados</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={Math.max(280, linksData.length * 40)}>
                  <BarChart
                    data={linksData}
                    layout="vertical"
                    margin={{ left: 10, right: 40, top: 5, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="nombre"
                      type="category"
                      width={200}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => [value, name === 'clics' ? 'Clics' : 'Usuarios únicos']}
                      labelFormatter={(label: string) => {
                        const item = linksData.find(d => d.nombre === label)
                        return item?.url_destino || label
                      }}
                    />
                    <Bar dataKey="clics" fill="#2fbc80" name="clics" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="usuarios_unicos" fill="#57a6d8" name="usuarios_unicos" radius={[0, 4, 4, 0]} />
                    <Legend formatter={(value) => value === 'clics' ? 'Clics' : value === 'usuarios_unicos' ? 'Usuarios únicos' : value} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

function truncateUrl(url: string, max = 40): string {
  try {
    const u = new URL(url)
    const path = u.pathname + u.search
    const short = u.host + (path.length > max - u.host.length ? path.slice(0, max - u.host.length) + '…' : path)
    return short
  } catch {
    return url.length > max ? url.slice(0, max) + '…' : url
  }
}

function KPICard({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: number }) {
  return (
    <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
      <CardContent className="pt-6">
        <div className="flex items-center gap-4">
          <div className="p-2 rounded-lg bg-teal-500/10">
            <Icon className="h-5 w-5 text-teal-500" />
          </div>
          <div>
            <p className="text-sm text-gray-500">{label}</p>
            <p className="text-2xl font-bold text-black dark:text-white">{value.toLocaleString()}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
